import { LocalDate, Month } from "@js-joda/core";
import React, { PropsWithChildren } from "react";
import { DayTimeParam } from "../../../messages/PatientTasks";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import {
  changeSchedule,
  decreaseScheduleByMonth,
  increaseScheduleByMonth,
} from "../../../utils/handleScheduleDates";
import { withDefault } from "../../../utils/withDefault";
import MonthPicker from "../../MonthPicker/MonthPicker";
import { AppButton } from "../AppButton";
import { AppAbstractModal } from "../AppModal/AppAbstractModal";
import { If } from "../If";
import { IsDefined } from "../IsDefined";
import { MfCalendarTileProperties } from "./CalendarPickerTypes";
import Calendar from "./components/Calendar";

export interface CalendarPickerProps {
  title?: string;
  header?: React.ReactNode;
  selectedDate: LocalDate | null;
  month: Month;
  year: number;
  isOpen: boolean;
  dayTimeParams: DayTimeParam[] | null;
  cancelText?: string;
  okText?: string;
  minDate?: LocalDate;
  maxDate?: LocalDate;
  onChangeMonth: (month: Month) => void;
  onChangeYear: (year: number) => void;
  onChoose?: (selectedDate: LocalDate) => void;
  onSubmit?: (selectedDate: LocalDate) => void;
  onCancel?: () => void;
}

const CalendarPicker = (props: PropsWithChildren<CalendarPickerProps>) => {
  const handleClickPreviousMonth = React.useCallback(() => {
    return decreaseScheduleByMonth(
      props.month,
      props.year,
      props.onChangeMonth,
      props.onChangeYear
    );
  }, [props.month, props.onChangeMonth, props.onChangeYear, props.year]);

  const handleClickNextMonth = React.useCallback(() => {
    return increaseScheduleByMonth(
      props.month,
      props.year,
      props.onChangeMonth,
      props.onChangeYear
    );
  }, [props.month, props.onChangeMonth, props.onChangeYear, props.year]);

  const handleChangeMonth = React.useCallback(
    (newYear: number, newMonth: number) => {
      return changeSchedule(
        props.month,
        props.year,
        newMonth,
        newYear,
        props.onChangeMonth,
        props.onChangeYear
      );
    },
    [props.month, props.onChangeMonth, props.onChangeYear, props.year]
  );

  const handleClickSubmit = React.useCallback(() => {
    if (props.onSubmit !== undefined && props.selectedDate !== null) {
      props.onSubmit.call(null, props.selectedDate);
    }
  }, [props.onSubmit, props.selectedDate]);

  const activeStartDate = React.useMemo(() => {
    return LocalDate.of(props.year, props.month, 1);
  }, [props.year, props.month]);

  const tileClassName = React.useCallback(
    (dateAndView: MfCalendarTileProperties) => {
      const disbaledTileClass = "disabled-tile";
      if (props.minDate !== undefined && dateAndView.date.isBefore(props.minDate)) {
        return disbaledTileClass;
      }
      if (props.maxDate !== undefined && dateAndView.date.isAfter(props.maxDate)) {
        return disbaledTileClass;
      }

      if (props.dayTimeParams !== null) {
        const dayOfWeekName = dateAndView.date.dayOfWeek().name();
        const shouldDisable = !props.dayTimeParams.some(
          (x) => x.dayOfWeek.name() === dayOfWeekName
        );
        if (shouldDisable) {
          return disbaledTileClass;
        }
      }
      return "";
    },
    [props.dayTimeParams, props.maxDate, props.minDate]
  );

  return (
    <AppAbstractModal
      isOpen={props.isOpen}
      className="CalendarPicker"
      onRequestClose={props.onCancel}
    >
      <div className="calendar-picker-container">
        <h3>{withDefault(props.title, translate(STRINGS.PICK_A_DATE))}</h3>
        {props.header}
        <MonthPicker
          year={props.year}
          month={props.month}
          onClickPrevious={handleClickPreviousMonth}
          onClickNext={handleClickNextMonth}
          onChange={handleChangeMonth}
        />
        <Calendar
          showNavigation={false}
          activeStartDate={activeStartDate}
          onClickDay={props.onChoose}
          value={props.selectedDate}
          tileClassName={tileClassName}
        />
        <If condition={props.children !== undefined}>
          <div className="children">{props.children}</div>
        </If>

        <div className="actions">
          <IsDefined value={props.onCancel}>
            {(onCancel) => (
              <AppButton variant="outline" onClick={onCancel}>
                {withDefault(props.cancelText, translate(STRINGS.CANCEL))}
              </AppButton>
            )}
          </IsDefined>

          <IsDefined value={props.onSubmit}>
            {() => (
              <AppButton
                variant="filled"
                disabled={props.selectedDate === null}
                onClick={handleClickSubmit}
              >
                {withDefault(props.okText, translate(STRINGS.SUBMIT))}
              </AppButton>
            )}
          </IsDefined>
        </div>
      </div>
    </AppAbstractModal>
  );
};

export default CalendarPicker;
